import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, Search, Building2, User } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import TextStack from './TextStack';

/**
 * VendorSelectorBeta - A modern vendor/contact selector with multi-select support
 *
 * @param {Array} value - Array of selected vendor IDs
 * @param {Function} onChange - Callback when selection changes (receives array of IDs)
 * @param {Array} vendors - Array of vendor objects with { id, name, companyName, firstName, lastName, email, type }
 * @param {String} placeholder - Placeholder text when no vendors selected
 * @param {Boolean} multiple - Allow multiple vendor selection (default: true)
 * @param {String} className - Additional CSS classes
 * @param {Boolean} showType - Show company/individual type badge (default: true)
 * @param {Boolean} grouped - Show filter tabs to filter by vendor type (default: false)
 */
const VendorSelectorBeta = ({
	value = [],
	onChange,
	vendors = [],
	placeholder = 'Select vendors...',
	multiple = true,
	className = '',
	showType = true,
	grouped = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'company' | 'individual'

	const selectedIds = Array.isArray(value) ? value : value ? [value] : [];

	// Clear search and reset filter when closing
	useEffect(() => {
		if (!isOpen) {
			setSearchQuery('');
			setTypeFilter('all');
		}
	}, [isOpen]);

	// Process vendors to have consistent display names
	const processedVendors = useMemo(() => {
		return vendors.map((vendor) => {
			// Use pre-calculated isCompany if available, otherwise determine from type/companyName
			const isCompany =
				typeof vendor.isCompany === 'boolean'
					? vendor.isCompany
					: vendor.type === 'company' || !!vendor.companyName;
			const displayName = isCompany
				? vendor.companyName || vendor.name
				: `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim() ||
					vendor.name ||
					'Unknown';

			return {
				...vendor,
				displayName,
				isCompany,
				secondaryText: isCompany
					? vendor.email || ''
					: vendor.companyName || vendor.email || '',
			};
		});
	}, [vendors]);

	// Filter vendors based on search query and type filter
	const filteredVendors = useMemo(() => {
		let result = processedVendors;

		// Apply type filter when grouped mode is enabled
		if (grouped && typeFilter !== 'all') {
			result = result.filter((vendor) =>
				typeFilter === 'company' ? vendor.isCompany : !vendor.isCompany,
			);
		}

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			result = result.filter((vendor) => {
				const searchableFields = [
					vendor.displayName,
					vendor.companyName,
					vendor.firstName,
					vendor.lastName,
					vendor.email,
					vendor.name,
				];

				return searchableFields.some(
					(field) => field && field.toString().toLowerCase().includes(query),
				);
			});
		}

		return result;
	}, [processedVendors, searchQuery, grouped, typeFilter]);

	// Group vendors by type (Company vs Individual)
	const groupedVendors = useMemo(() => {
		const companies = filteredVendors.filter((v) => v.isCompany);
		const individuals = filteredVendors.filter((v) => !v.isCompany);

		return { companies, individuals };
	}, [filteredVendors]);

	const handleToggle = (vendorId) => {
		if (!multiple) {
			onChange(vendorId);
			setIsOpen(false);
			return;
		}

		const newValue = selectedIds.includes(vendorId)
			? selectedIds.filter((id) => id !== vendorId)
			: [...selectedIds, vendorId];
		onChange(newValue);
	};

	const handleClear = () => {
		onChange(multiple ? [] : null);
	};

	// Get display text for trigger button
	const getDisplayText = () => {
		if (selectedIds.length === 0) {
			return <span className="text-gray-400">{placeholder}</span>;
		}

		if (selectedIds.length === 1) {
			const vendor = processedVendors.find((v) => v.id === selectedIds[0]);
			if (!vendor) return <span className="text-gray-400">{placeholder}</span>;

			return (
				<div className="flex items-center gap-2">
					{showType &&
						(vendor.isCompany ? (
							<Building2 className="h-4 w-4 text-blue-500 shrink-0" />
						) : (
							<User className="h-4 w-4 text-gray-400 shrink-0" />
						))}
					<TextStack
						primary={vendor.displayName}
						secondary={vendor.secondaryText}
						size="sm"
						inline
					/>
				</div>
			);
		}

		return (
			<span className="text-gray-900">
				{selectedIds.length} vendors selected
			</span>
		);
	};

	// Render vendor item
	const renderVendorItem = (vendor) => {
		const checked = selectedIds.includes(vendor.id);

		return (
			<div
				key={vendor.id}
				className={`flex items-start gap-2 px-2 py-1.5 rounded-sm hover:bg-gray-100 cursor-pointer ${
					checked && !multiple ? 'bg-indigo-50' : ''
				}`}
				onClick={() => handleToggle(vendor.id)}
			>
				{multiple && (
					<input
						type="checkbox"
						checked={checked}
						onChange={() => handleToggle(vendor.id)}
						className="mt-1 h-4 w-4 rounded-lg border-gray-300 text-indigo-600 focus:ring-indigo-500"
						onClick={(e) => e.stopPropagation()}
					/>
				)}
				<div className="flex items-start gap-2 flex-1 min-w-0">
					{showType &&
						(vendor.isCompany ? (
							<Building2 className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
						) : (
							<User className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
						))}
					<div className="flex-1 min-w-0">
						<TextStack
							primary={vendor.displayName}
							secondary={vendor.secondaryText}
							size="sm"
						/>
					</div>
				</div>
			</div>
		);
	};

	// Render vendors list
	const renderVendors = () => {
		const { companies, individuals } = groupedVendors;
		const hasResults = companies.length > 0 || individuals.length > 0;

		// When a specific type filter is active, show flat list without headers
		const showFlatList = grouped && typeFilter !== 'all';

		return (
			<div className="h-[288px] overflow-y-auto shadow-inner">
				<div className="p-2">
					{!hasResults ? (
						<div className="flex flex-col items-center justify-center h-full text-gray-500 py-8">
							<p className="text-sm">No vendors found</p>
							{searchQuery && (
								<p className="text-xs mt-1">Try a different search term</p>
							)}
						</div>
					) : showFlatList ? (
						// Flat list when filtered by type
						<div>{filteredVendors.map(renderVendorItem)}</div>
					) : (
						// Grouped list showing both types with headers
						<>
							{companies.length > 0 && (
								<div>
									<p className="px-2 pt-2 mb-2 text-xxs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1">
										<Building2 className="h-3 w-3" />
										Companies
									</p>
									{companies.map(renderVendorItem)}
								</div>
							)}
							{individuals.length > 0 && (
								<div>
									<p className="px-2 pt-4 mb-2 text-xxs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1">
										<User className="h-3 w-3" />
										Individuals
									</p>
									{individuals.map(renderVendorItem)}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className={className}>
			<Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[40px]"
					>
						<span className="flex items-center gap-2 truncate flex-1 min-w-0">
							{getDisplayText()}
						</span>
						<ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg ring-1 ring-black ring-opacity-5 bg-white"
					align="start"
				>
					{/* Search Input */}
					<div className="p-3 border-b border-gray-200 bg-white">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search vendors..."
								className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>
						{/* Type Filter Tabs */}
						{grouped && (
							<div className="flex gap-1 mt-2">
								{[
									{ key: 'all', label: 'All' },
									{ key: 'company', label: 'Companies', icon: Building2 },
									{ key: 'individual', label: 'Individuals', icon: User },
								].map(({ key, label, icon: Icon }) => (
									<button
										key={key}
										type="button"
										onClick={() => setTypeFilter(key)}
										className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
											typeFilter === key
												? 'bg-indigo-100 text-indigo-700'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
										}`}
									>
										{Icon && <Icon className="h-3 w-3" />}
										{label}
									</button>
								))}
							</div>
						)}
					</div>

					{/* Content */}
					{renderVendors()}

					{/* Footer */}
					{multiple && selectedIds.length > 0 && (
						<div className="flex items-center justify-between border-t px-3 py-2 bg-gray-50">
							<span className="text-xs text-gray-500">
								{selectedIds.length} selected
							</span>
							<button
								type="button"
								onClick={handleClear}
								className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
							>
								Clear
							</button>
						</div>
					)}
				</PopoverContent>
			</Popover>
		</div>
	);
};

export default VendorSelectorBeta;
